/* eslint-disable */
// Hive census — what the network reports about itself, with its sample.
//
// This file does one thing: fetch, poll, hand the result to the view. Rendering
// lives in pages/hive/HiveCensusView.js, threshold arithmetic in
// pages/hive/thresholds.js. Split so each can be tested for what it does rather
// than how it is spelled.
//
// Reads /api/social/hive-census, which serves what receive_peer_delta already
// verified (version, freshness, guardrail hash, Ed25519, HMAC, origin
// attestation). Nothing is computed here that the network did not report.
import React from 'react';
import { Link } from 'react-router-dom';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import HeaderNano from '../components/shared/SeoHeader';
import FooterLight from '../components/shared/SeoFooter';
import Spacer from '../components/Agent/Spacer';
import { SOCIAL_API_URL } from '../config/apiBase';
import { Stat, NextThreshold, Projection, Ladder, PerNode, TensorBoard, JoinHive } from './hive/HiveCensusView';
import { position } from './hive/thresholds';
import PageMeta from '../components/shared/PageMeta';
import { readPrerenderData, publishPrerenderData } from '../utils/prerenderData';

// /api/hive/* is not routed by the gateway; /api/social is.
const CENSUS_URL = `${SOCIAL_API_URL}/hive-census`;
const POLL_MS = 30000;

const STATE_COPY = {
  not_federating: 'This node is not federating, so it has no peers to count.',
  no_peers: 'Federating, and no peer has reported yet.',
  unreachable: 'The census endpoint did not answer.',
  error: 'The census endpoint returned an error.',
};

export default function HiveCensus() {
  // Seeded from what the prerenderer got, so the first render here matches the
  // saved HTML instead of showing a spinner against it. Read in the initialiser,
  // not an effect: an effect arrives one render too late, which is the mismatch
  // this removes. See src/utils/prerenderData.js.
  const seed = readPrerenderData('hiveCensus');
  const [data, setData] = React.useState(seed ? seed.data : null);
  const [state, setState] = React.useState(seed ? seed.state : 'loading');

  React.useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await fetch(CENSUS_URL);
        const body = await res.json();
        if (!alive) return;
        const next = body && body.status ? body.status : 'error';
        setData(body);
        setState(next);
        publishPrerenderData('hiveCensus', { data: body, state: next });
      } catch (e) {
        if (alive) setState('unreachable');
        // Published on failure too. The prerenderer's fetch DID fail on
        // 2026-08-04 and the saved page showed "unreachable"; without this the
        // client would start at 'loading' and disagree with it all over again.
        publishPrerenderData('hiveCensus', { data: null, state: 'unreachable' });
      }
    };
    load();
    const t = setInterval(load, POLL_MS);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const d = data || {};
  const nodes = d.nodes_reporting ?? 0;
  // Cumulative distinct genuine installs that ever joined the hive (monotonic,
  // persistent), as opposed to `nodes` which is only those active in the
  // window. This is the honest figure the fleet target is measured against.
  const installs = d.total_installs;
  const fmt = (v, p = 3) => (v === null || v === undefined ? null : Number(v).toFixed(p));
  // A growth rate is a ratio between joins, so one indexed node has none. The
  // census still sends 1.0 for it; showing that as "1.000x" contradicted the
  // Projection card beside it, which says one node is a reading, not a trend.
  const hasGrowth = Number(d.nodes_with_intelligence) >= 2;

  return (
    <React.Fragment>
      <PageMeta
        title="Hive census: is the network getting better? | Hevolve"
        description="Live figures from the HART OS hive: nodes reporting, collective intelligence index, growth per join, and distance to the next documented threshold. Measured values and projections are labelled separately."
        path="/hive"
      />
      <HeaderNano />
      <Container maxWidth="lg" sx={{ pt: 6 }} data-testid="hive-census">
        <Typography variant="h3" sx={{ color: '#fff', fontWeight: 700 }}>
          Is the network getting better?
        </Typography>
        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.65)', mt: 1, maxWidth: 760 }}>
          Every node broadcasts a signed delta and every receiver verifies it
          before counting it. This shows what was counted and how many nodes it
          came from. Measured figures and projections are labelled separately,
          and a projection with no basis is not shown.
        </Typography>
        <Typography variant="body2" sx={{ mt: 1.5 }}>
          <Box
            component={Link}
            to="/how_it_works"
            data-testid="hive-how-it-works-link"
            sx={{ color: '#8b82ff', fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            How a node learns, and what it shares &rarr;
          </Box>
        </Typography>

        {state === 'loading' ? (
          // aria-busy is what the prerenderer waits on.
          //
          // scripts/prerender.js decides a route is done when nothing under
          // #root carries aria-busy="true" or .MuiSkeleton-root. This spinner
          // was a bare Box with neither, so the gate passed on its FIRST poll
          // and the page was captured while the census fetch was still in
          // flight -- the fetch takes ~100ms and the capture beat it. Every
          // saved /hive was therefore this spinner: no figures for a crawler,
          // and no hiveCensus seed, so each visitor also started at 'loading'.
          // Same shape as /social/challenges, which the gate was widened for.
          //
          // The gate captures anyway after 15s, so the worst case here is the
          // behaviour we already had.
          <Box
            sx={{ display: 'flex', justifyContent: 'center', py: 10 }}
            data-testid="census-loading"
            aria-busy="true"
          >
            <CircularProgress />
          </Box>
        ) : (
          <React.Fragment>
            {state !== 'ok' ? (
              <Card
                data-testid="census-state"
                sx={{
                  mt: 3, background: 'rgba(26,26,46,0.9)',
                  border: '1px solid rgba(255,183,77,0.3)', borderRadius: 3,
                }}
              >
                <CardContent>
                  <Chip
                    label={String(state).replace(/_/g, ' ')}
                    sx={{ color: '#ffb74d', border: '1px solid #ffb74d', mb: 1.5 }}
                  />
                  <Typography sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    {STATE_COPY[state] || 'No reading.'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', display: 'block', mt: 1.5 }}>
                    A state, not a zero. A hive with nobody in it and a hive that
                    is failing look different, and they read differently here.
                    The ladder below shows what is being waited for.
                  </Typography>
                </CardContent>
              </Card>
            ) : null}

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Stat
                  testid="stat-installs"
                  label="Genuine installs"
                  value={installs ?? null}
                  note="cumulative, gate-verified"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Stat
                  testid="stat-nodes"
                  label="Reporting now"
                  value={state === 'ok' ? nodes : null}
                  note={`active within ${Math.round((d.window_seconds || 3600) / 60)} min`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Stat
                  testid="stat-counted"
                  label="Counted in the mean"
                  value={state === 'ok' ? d.nodes_with_intelligence : null}
                  note={d.nodes_stale ? `${d.nodes_stale} stale, excluded` : 'none stale'}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Stat
                  testid="stat-index"
                  label="Collective index"
                  value={fmt(d.mean_intelligence_index)}
                  note="knowledge capacity, not capability"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Stat
                  testid="stat-growth"
                  label="Growth per join"
                  value={hasGrowth ? fmt(d.mean_growth_rate) : null}
                  suffix="x"
                  note={hasGrowth ? 'above 1.0 is compounding' : 'needs two nodes with an index'}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <NextThreshold nodes={nodes} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Projection
                  growthRate={d.mean_growth_rate}
                  nodesWithIndex={d.nodes_with_intelligence}
                />
              </Grid>

              <Grid item xs={12}>
                <JoinHive remaining={position(nodes).remaining} />
              </Grid>

              <Grid item xs={12}>
                <Ladder nodes={nodes} />
              </Grid>

              {d.tensorboard_url ? (
                <Grid item xs={12}>
                  <TensorBoard url={d.tensorboard_url} />
                </Grid>
              ) : null}

              {Object.keys(d.per_node || {}).length ? (
                <Grid item xs={12}>
                  <PerNode perNode={d.per_node} />
                </Grid>
              ) : null}
            </Grid>

            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'block', mt: 3 }}>
              The index is a knowledge-capacity figure from concept-graph
              topology: log2(paths + 1) x (1 + depth/10) x (learned/concepts). It
              measures what the graph can express, not how well a node answers.
              Nodes past the freshness window are listed and excluded from the
              means. Refreshes every {POLL_MS / 1000} seconds.
            </Typography>
          </React.Fragment>
        )}
      </Container>
      <Spacer h={120} />
      <FooterLight />
    </React.Fragment>
  );
}
